import { Button } from "@/components/flexkit/Button";
import { Combobox } from "@/components/flexkit/combobox";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import {
    useAuthenticatedRunnerIdsForHosts,
    useListRunners,
    useListSCMOrganizationsAcrossHosts,
    useSearchRepositoriesAcrossHosts,
} from "@/queries/runner-queries";
import { useListAllRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import type { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { SearchMode, RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { TrackLocations } from "@/hooks/use-segment";
import { IconTriangleExclamation } from "central-icons/IconTriangleExclamation";
import { useCallback, useMemo, useState, type FC } from "react";
import { useDebounce } from "use-debounce";
import {
    extractHost,
    extractRepoPath,
    getDefaultScmHost,
    getScopeConfig,
    getWebhookSettingsUrl,
    providerToScmId,
    scmRepoToScope,
    scopeToDisplayString,
    WEBHOOK_PROVIDER_CONFIG,
    type SCMRepository,
} from "@/components/webhooks/webhook-config";
import { IconClose } from "@/assets/icons/geist/IconClose";

export type WebhookScopeEntry = {
    host: string;
    owner: string;
    name: string;
};

export type WebhookOrgScope = {
    host: string;
    name: string;
};

type WebhookScopeFieldProps = {
    provider: WebhookProvider;
    webhookType: WebhookType;
    /** Legacy single-scope value, used for org type and backward compatibility */
    value: string;
    onChange: (value: string) => void;
    /** Multi-scope entries for repository type */
    scopes?: WebhookScopeEntry[];
    onScopesChange?: (scopes: WebhookScopeEntry[]) => void;
    /** Structured organization scope */
    organizationScope?: WebhookOrgScope;
    onOrganizationScopeChange?: (scope: WebhookOrgScope | undefined) => void;
    disabled?: boolean;
    readOnly?: boolean;
    /** If true, the settings URL will point to the "new webhook" page */
    isNewWebhook?: boolean;
};

export const WebhookScopeField: FC<WebhookScopeFieldProps> = ({
    provider,
    webhookType,
    value,
    onChange,
    scopes = [],
    onScopesChange,
    organizationScope,
    onOrganizationScopeChange,
    disabled = false,
    readOnly = false,
    isNewWebhook = false,
}) => {
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue] = useDebounce(searchValue, 400, { trailing: true });
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);

    const providerName = WEBHOOK_PROVIDER_CONFIG[provider].name;
    const { isOrgType, scopeLabel, comboboxPlaceholder } = getScopeConfig(webhookType);

    // Only load data if not read-only
    const shouldLoadData = !readOnly;

    // Get runners for querying: active, non-local runners
    const { runners: allRunners } = useListRunners({ enabled: shouldLoadData });
    const runners = useMemo(
        () => allRunners.filter((r) => r.status?.phase === RunnerPhase.ACTIVE && r.kind !== RunnerKind.LOCAL),
        [allRunners],
    );
    const runnerIds = useMemo(() => runners.map((r) => r.runnerId), [runners]);

    // Discover SCM hosts from runner integrations matching this provider
    const scmId = providerToScmId(provider);
    const { data: allIntegrations } = useListAllRunnerSCMIntegrations(
        shouldLoadData && runnerIds.length > 0 ? runnerIds : undefined,
    );
    const scmHosts = useMemo(() => {
        if (!allIntegrations) {
            const defaultHost = getDefaultScmHost(provider);
            return defaultHost ? [defaultHost] : [];
        }
        const hosts = new Set<string>();
        for (const integration of allIntegrations) {
            if (integration.scmId === scmId && integration.host) {
                hosts.add(integration.host);
            }
        }
        if (hosts.size === 0) {
            const defaultHost = getDefaultScmHost(provider);
            if (defaultHost) hosts.add(defaultHost);
        }
        return Array.from(hosts);
    }, [allIntegrations, scmId, provider]);

    // Derive the host for settings URL from the actual scope data
    const settingsHost = isOrgType
        ? organizationScope?.host || scmHosts[0] || ""
        : scopes[0]?.host || scmHosts[0] || "";
    const settingsUrl = getWebhookSettingsUrl(
        provider,
        webhookType,
        value || (scopes[0] ? scopeToDisplayString(scopes[0]) : ""),
        isNewWebhook,
        settingsHost,
    );

    // Pre-filter to only runners authenticated for any of the discovered SCM hosts
    const { authenticatedRunnerIds } = useAuthenticatedRunnerIdsForHosts({
        runnerIds,
        scmHosts,
        enabled: shouldLoadData && runnerIds.length > 0 && scmHosts.length > 0,
    });

    // Fetch organizations from authenticated runners across all discovered hosts
    const { organizations, isFetching: isFetchingOrgs } = useListSCMOrganizationsAcrossHosts({
        runnerIds: authenticatedRunnerIds,
        scmHosts,
        enabled: shouldLoadData && isOrgType && authenticatedRunnerIds.length > 0 && scmHosts.length > 0,
    });

    // Fetch repositories from authenticated runners across all discovered hosts
    const { repositories: repoResults, isLoading: isLoadingRepos } = useSearchRepositoriesAcrossHosts({
        runnerIds: authenticatedRunnerIds,
        query: debouncedSearchValue,
        scmHosts,
        searchMode: SearchMode.KEYWORD,
        enabled: shouldLoadData && !isOrgType && authenticatedRunnerIds.length > 0 && scmHosts.length > 0,
        pageSize: 50,
    });

    const repositories = useMemo(
        (): SCMRepository[] =>
            repoResults.map((repo) => ({
                name: repo.name,
                url: repo.url,
            })),
        [repoResults],
    );

    // Filter out already-selected repositories
    const availableRepositories = useMemo(() => {
        const selectedPaths = new Set(scopes.map((s) => scopeToDisplayString(s)));
        return repositories.filter((repo) => !selectedPaths.has(extractRepoPath(repo.url)));
    }, [repositories, scopes]);

    // Only update search value while the popover is open.
    const handleSearchValueChanged = useCallback(
        (newValue: string) => {
            if (isPopoverOpen) {
                setSearchValue(newValue);
            }
        },
        [isPopoverOpen],
    );

    const handleOpenChange = useCallback((open: boolean) => {
        setIsPopoverOpen(open);
        if (!open) {
            setSearchValue("");
        }
    }, []);

    const handleRepoSelected = useCallback(
        (repoPath: string) => {
            const repo = repositories.find((r) => extractRepoPath(r.url) === repoPath);
            if (!repo || !onScopesChange) return;

            const scopeEntry = scmRepoToScope(repo);
            const alreadyExists = scopes.some((s) => s.owner === scopeEntry.owner && s.name === scopeEntry.name);
            if (!alreadyExists) {
                onScopesChange([...scopes, scopeEntry]);
            }
            // Also update legacy value for backward compatibility
            if (!value) {
                onChange(repoPath);
            }
        },
        [repositories, scopes, onScopesChange, value, onChange],
    );

    const handleRemoveScopeClick = useCallback((index: number) => {
        setPendingRemoveIndex(index);
    }, []);

    const handleConfirmRemoveScope = useCallback(() => {
        if (pendingRemoveIndex === null || !onScopesChange) return;
        const newScopes = scopes.filter((_, i) => i !== pendingRemoveIndex);
        onScopesChange(newScopes);
        if (newScopes.length > 0) {
            onChange(scopeToDisplayString(newScopes[0]));
        } else {
            onChange("");
        }
        setPendingRemoveIndex(null);
    }, [pendingRemoveIndex, scopes, onScopesChange, onChange]);

    const handleCancelRemoveScope = useCallback(() => {
        setPendingRemoveIndex(null);
    }, []);

    const handleOpenSettings = () => {
        if (settingsUrl) {
            window.open(settingsUrl, "_blank", "noopener,noreferrer");
        }
    };

    // For org type, keep the existing single-select behavior
    if (isOrgType) {
        return (
            <div className="flex flex-col gap-2">
                <Label htmlFor="webhook-scope" className="text-sm font-medium">
                    {scopeLabel}
                </Label>
                <div className="flex items-center gap-1">
                    {readOnly ? (
                        <Input
                            id="webhook-scope"
                            value={value}
                            readOnly
                            className="flex-1"
                            data-testid="webhook-scope-input"
                        />
                    ) : (
                        <Combobox
                            value={organizationScope?.host ? `${organizationScope.host}/${value}` : value}
                            onValueChange={(compositeVal) => {
                                const matchedOrg = organizations.find(
                                    (o) => `${extractHost(o.url)}/${o.name}` === compositeVal,
                                );
                                const orgName = matchedOrg?.name ?? compositeVal;
                                onChange(orgName);
                                if (onOrganizationScopeChange && matchedOrg) {
                                    const orgHost = extractHost(matchedOrg.url);
                                    onOrganizationScopeChange({ host: orgHost, name: matchedOrg.name });
                                } else if (onOrganizationScopeChange && !compositeVal) {
                                    onOrganizationScopeChange(undefined);
                                }
                            }}
                            onOpenChange={handleOpenChange}
                            loading={false}
                            className="flex-1"
                            disabled={disabled}
                        >
                            <Combobox.Value>
                                {(val) => {
                                    // Display just the org name, stripping the host/ prefix
                                    const display = val.includes("/") ? val.slice(val.indexOf("/") + 1) : val;
                                    return (
                                        <Combobox.ValueLabel className="max-w-40 truncate">
                                            {display || comboboxPlaceholder}
                                        </Combobox.ValueLabel>
                                    );
                                }}
                            </Combobox.Value>
                            <Combobox.Popover sameWidth={false} className="min-w-[--popover-anchor-width] max-w-96">
                                <Combobox.SearchBox loading={isFetchingOrgs} />
                                {organizations && organizations.length > 0 ? (
                                    <Combobox.List
                                        items={organizations}
                                        searchKeys={["name"]}
                                        noMatchesComponent={<Combobox.Empty>No organizations found</Combobox.Empty>}
                                    >
                                        {(org) => {
                                            const host = extractHost(org.url);
                                            const compositeValue = `${host}/${org.name}`;
                                            const hasDuplicate = organizations.some(
                                                (o) => o.name === org.name && o.url !== org.url,
                                            );
                                            const label = hasDuplicate ? `${org.name} (${host})` : org.name;
                                            return (
                                                <Combobox.ListItem
                                                    key={org.url}
                                                    value={compositeValue}
                                                    title={label}
                                                />
                                            );
                                        }}
                                    </Combobox.List>
                                ) : (
                                    <Combobox.Empty>No organizations found</Combobox.Empty>
                                )}
                            </Combobox.Popover>
                        </Combobox>
                    )}
                    <Button
                        variant="ghost"
                        onClick={handleOpenSettings}
                        disabled={!settingsUrl || disabled}
                        data-testid="open-webhook-settings-org"
                        data-tracking-id="open-webhook-settings-org"
                        className="gap-0 px-3 text-sm"
                    >
                        Open&nbsp;{providerName}&nbsp;webhook settings
                    </Button>
                </div>
            </div>
        );
    }

    // Repository type: multi-scope selection
    return (
        <div className="flex flex-col gap-2">
            <Label htmlFor="webhook-scope" className="text-sm font-medium">
                Repositories
            </Label>

            {/* Selected scopes list */}
            {scopes.length > 0 && (
                <div className="flex flex-col gap-1" data-testid="webhook-scopes-list">
                    {scopes.map((scope, index) => {
                        const repoSettingsUrl = getWebhookSettingsUrl(
                            provider,
                            webhookType,
                            scopeToDisplayString(scope),
                            isNewWebhook,
                            scope.host,
                        );
                        return (
                            <div key={`${scope.owner}/${scope.name}`} className="flex items-center gap-1">
                                <div className="border-pk-border bg-pk-surface-secondary flex flex-1 items-center gap-2 rounded-md border px-3 py-1.5 text-sm">
                                    <span className="flex-1 truncate">{scopeToDisplayString(scope)}</span>
                                    {!readOnly && !disabled && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveScopeClick(index)}
                                            className="text-pk-content-tertiary hover:text-pk-content-primary"
                                            aria-label={`Remove ${scopeToDisplayString(scope)}`}
                                            data-testid={`remove-scope-${index}`}
                                            data-tracking-id={`remove-webhook-scope-${index}`}
                                        >
                                            <IconClose size="sm" />
                                        </button>
                                    )}
                                </div>
                                <Button
                                    variant="ghost"
                                    onClick={() => {
                                        if (repoSettingsUrl) {
                                            window.open(repoSettingsUrl, "_blank", "noopener,noreferrer");
                                        }
                                    }}
                                    disabled={!repoSettingsUrl || disabled}
                                    data-testid={`open-webhook-settings-${index}`}
                                    data-tracking-id={`open-webhook-settings-${index}`}
                                    className="gap-0 px-3 text-sm"
                                >
                                    Open&nbsp;{providerName}&nbsp;webhook settings
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add repository combobox */}
            {!readOnly && (
                <Combobox
                    value=""
                    onValueChange={handleRepoSelected}
                    onOpenChange={handleOpenChange}
                    loading={false}
                    disabled={disabled}
                >
                    <Combobox.Value>
                        {() => (
                            <Combobox.ValueLabel className="max-w-40 truncate">
                                {comboboxPlaceholder}
                            </Combobox.ValueLabel>
                        )}
                    </Combobox.Value>
                    <Combobox.Popover sameWidth={false} className="max-w-96">
                        <Combobox.SearchBox onValueChanged={handleSearchValueChanged} loading={isLoadingRepos} />
                        {availableRepositories && availableRepositories.length > 0 ? (
                            <Combobox.List
                                items={availableRepositories}
                                searchKeys={[]}
                                disableFiltering
                                noMatchesComponent={<Combobox.Empty>No repositories found</Combobox.Empty>}
                            >
                                {(repo) => (
                                    <Combobox.ListItem
                                        key={repo.url}
                                        value={extractRepoPath(repo.url)}
                                        title={extractRepoPath(repo.url)}
                                    />
                                )}
                            </Combobox.List>
                        ) : (
                            <Combobox.Empty>No repositories found</Combobox.Empty>
                        )}
                    </Combobox.Popover>
                </Combobox>
            )}

            {/* Confirmation dialog for removing a repository scope */}
            {pendingRemoveIndex !== null && scopes[pendingRemoveIndex] && (
                <RemoveScopeConfirmDialog
                    scope={scopes[pendingRemoveIndex]}
                    provider={provider}
                    webhookType={webhookType}
                    providerName={providerName}
                    isNewWebhook={isNewWebhook}
                    onConfirm={handleConfirmRemoveScope}
                    onCancel={handleCancelRemoveScope}
                />
            )}
        </div>
    );
};

type RemoveScopeConfirmDialogProps = {
    scope: WebhookScopeEntry;
    provider: WebhookProvider;
    webhookType: WebhookType;
    providerName: string;
    isNewWebhook: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

const RemoveScopeConfirmDialog: FC<RemoveScopeConfirmDialogProps> = ({
    scope,
    provider,
    webhookType,
    providerName,
    isNewWebhook,
    onConfirm,
    onCancel,
}) => {
    const display = scopeToDisplayString(scope);
    const settingsUrl = getWebhookSettingsUrl(provider, webhookType, display, isNewWebhook, scope.host);

    return (
        <Dialog open onOpenChange={(open) => !open && onCancel()}>
            <DialogContent
                data-testid="remove-scope-confirm-dialog"
                data-track-location={TrackLocations.RemoveWebhookScopeDialog}
            >
                <DialogHeader>
                    <DialogTitle>Remove repository</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to remove <strong>{display}</strong> from this webhook?
                    </DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <div className="rounded-lg border border-border-base px-4 py-3 text-content-primary">
                        <div className="flex gap-3">
                            <IconTriangleExclamation size={16} className="mt-0.5 shrink-0 text-content-warning" />
                            <div className="flex flex-col gap-2">
                                <p>
                                    Remember to also remove the webhook from your {providerName} repository settings to
                                    stop sending events.
                                </p>
                                {settingsUrl && (
                                    <ExternalLink
                                        href={settingsUrl}
                                        iconSize="sm"
                                        data-testid="remove-scope-scm-settings-link"
                                        data-tracking-id="open-scm-settings-remove-scope-dialog"
                                    >
                                        {display}
                                    </ExternalLink>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="cancel-remove-scope-button">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        data-testid="confirm-remove-scope-button"
                        data-tracking-id="confirm-remove-webhook-scope"
                    >
                        Remove
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
